import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Spinner, Table, Badge } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const getItemKey = (item) =>
 [item.itemName || "", item.unit || "", item.description || ""]
 .map((v) => String(v).trim().toLowerCase())
 .filter(Boolean)
 .join("|");

// Resolve all available stages from a ContractualWorkOrder's payment terms
const getAvailableStages = (cwo) => {
 if (!cwo) return [];
 const stages = new Set();
 const mode = cwo.paymentMode || "CUSTOM";

 // Global structured terms
 (cwo.structuredPaymentTerms || []).forEach((t) => {
 const label = mode === "STANDARD" ? t.stage : t.name;
 if (label?.trim()) stages.add(label.trim());
 });

 // Item-level terms
 (cwo.selectedBoqItems || []).forEach((item) => {
 if (!item.paymentTerms?.length) return;
 const iMode = item.paymentMode || "CUSTOM";
 item.paymentTerms.forEach((t) => {
 const label = iMode === "STANDARD" ? t.stage : t.name;
 if (label?.trim()) stages.add(label.trim());
 });
 });

 return Array.from(stages);
};

// Get term % for a stage on a specific item (item-level → global fallback)
const getTermPercentage = (stageName, cwoItem, cwo) => {
 const norm = (s) => String(s || "").trim().toLowerCase();

 // Item-level
 if (cwoItem?.paymentTerms?.length) {
 const mode = cwoItem.paymentMode || "CUSTOM";
 const term = mode === "STANDARD"
 ? cwoItem.paymentTerms.find((t) => norm(t.stage) === norm(stageName))
 : cwoItem.paymentTerms.find((t) => norm(t.name) === norm(stageName));
 if (term) return Number(term.percentage || 0);
 }

 // Global fallback
 if (cwo?.structuredPaymentTerms?.length) {
 const mode = cwo.paymentMode || "CUSTOM";
 const term = mode === "STANDARD"
 ? cwo.structuredPaymentTerms.find((t) => norm(t.stage) === norm(stageName))
 : cwo.structuredPaymentTerms.find((t) => norm(t.name) === norm(stageName));
 if (term) return Number(term.percentage || 0);
 }

 return 0;
};

// Combined % for multiple stages on an item
const getTotalPercentage = (stageNames, cwoItem, cwo) => {
 return stageNames.reduce((sum, s) => sum + getTermPercentage(s, cwoItem, cwo), 0);
};

const ContractorBillingAdd = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const cwoFromUrl = searchParams.get("cwo") || "";
 const workOrderFromUrl = searchParams.get("workOrder") || "";

 const [cwos, setCwos] = useState([]);
 const [selectedCwoId, setSelectedCwoId] = useState(cwoFromUrl);
 const [selectedStages, setSelectedStages] = useState([]);
 const [invoiceNo, setInvoiceNo] = useState("");
 const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
 const [remarks, setRemarks] = useState("");
 const [billFile, setBillFile] = useState(null);
 const [items, setItems] = useState([]);
 const [existingBills, setExistingBills] = useState([]);
 const [approvedJMRQtyMap, setApprovedJMRQtyMap] = useState(new Map());
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 useEffect(() => {
 const load = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams({ status: "Approved" });
 if (workOrderFromUrl) params.append("workOrder", workOrderFromUrl);
 const res = await api.get(`/contractual-work-orders?${params.toString()}`);
 if (res.data.success) {
 setCwos(res.data.data || []);
 const firstId = cwoFromUrl || res.data.data?.[0]?._id || "";
 setSelectedCwoId(firstId);
 }
 } catch (error) {
 swal("Error", "Failed to load approved contractor WOs", "error");
 } finally {
 setLoading(false);
 }
 };
 load();
 }, [cwoFromUrl, workOrderFromUrl]);

 const selectedCwo = useMemo(
 () => cwos.find((cwo) => cwo._id === selectedCwoId),
 [cwos, selectedCwoId]
 );

 // Reset stages when CWO changes
 useEffect(() => {
 setSelectedStages([]);
 }, [selectedCwoId]);

 const availableStages = useMemo(
 () => getAvailableStages(selectedCwo),
 [selectedCwo]
 );

 useEffect(() => {
 const loadExistingBills = async () => {
 if (!selectedCwoId) {
 setExistingBills([]);
 setApprovedJMRQtyMap(new Map());
 return;
 }
 try {
 const [billRes, jmrRes] = await Promise.all([
 api.get(`/contractor-billings?contractualWorkOrder=${selectedCwoId}`),
 api.get(`/contractor-jmrs?contractualWorkOrder=${selectedCwoId}&approvalStatus=Approved&limit=1000`),
 ]);

 if (billRes.data.success) setExistingBills(billRes.data.data || []);

 // Build map: itemKey → total approved JMR qty
 if (jmrRes.data.success) {
 const qtyMap = new Map();
 (jmrRes.data.data || []).forEach((jmr) => {
 (jmr.items || []).forEach((item) => {
 const key = getItemKey(item);
 qtyMap.set(key, Number(qtyMap.get(key) || 0) + Number(item.measuredQuantity || 0));
 });
 });
 setApprovedJMRQtyMap(qtyMap);
 }
 } catch (error) {
 swal("Error", "Failed to load previous contractor bills", "error");
 }
 };
 loadExistingBills();
 }, [selectedCwoId]);

 const billedQuantityByItem = useMemo(() => {
 const billedMap = new Map();
 existingBills
 .filter((bill) => bill.status !== "Rejected")
 .forEach((bill) => {
 (bill.items || []).forEach((item) => {
 const itemKey = getItemKey(item);
 billedMap.set(
 itemKey,
 Number(billedMap.get(itemKey) || 0) + Number(item.quantity || 0)
 );
 });
 });
 return billedMap;
 }, [existingBills]);

 useEffect(() => {
 const nextItems = (selectedCwo?.selectedBoqItems || [])
 .map((item) => {
 const originalQuantity = Number(item.quantity || 0);
 const alreadyBilledQuantity = Number(billedQuantityByItem.get(getItemKey(item)) || 0);
 const remainingQuantity = Math.max(originalQuantity - alreadyBilledQuantity, 0);

 const jmrApprovedQty = Number(approvedJMRQtyMap.get(getItemKey(item)) || 0);
 // Pre-fill with JMR approved qty (capped at remaining), else full remaining
 const suggestedQty = jmrApprovedQty > 0
 ? Math.min(jmrApprovedQty, remainingQuantity)
 : remainingQuantity;

 const cwoPrice = Number(item.price || 0);
 const totalPercent = selectedStages.length > 0
 ? getTotalPercentage(selectedStages, item, selectedCwo)
 : 0;
 const computedPrice = selectedStages.length > 0 && totalPercent > 0
 ? (cwoPrice * totalPercent) / 100
 : cwoPrice;

 return {
 ...item,
 originalQuantity,
 originalPrice: cwoPrice,
 totalPercent,
 alreadyBilledQuantity,
 remainingQuantity,
 jmrApprovedQty,
 quantity: suggestedQty,
 price: computedPrice,
 };
 })
 .filter((item) => item.remainingQuantity > 0);

 setItems(nextItems);
 }, [selectedCwoId, selectedCwo, billedQuantityByItem, approvedJMRQtyMap, selectedStages]);

 const totals = useMemo(() => {
 const subTotal = items.reduce(
 (sum, item) => sum + Number(item.quantity || 0) * Number(item.price || 0),
 0
 );
 const cgstAmount = (subTotal * Number(selectedCwo?.cgst || 0)) / 100;
 const sgstAmount = (subTotal * Number(selectedCwo?.sgst || 0)) / 100;
 const igstAmount = (subTotal * Number(selectedCwo?.igst || 0)) / 100;
 const taxAmount = cgstAmount + sgstAmount + igstAmount;
 return { subTotal, cgstAmount, sgstAmount, igstAmount, taxAmount, totalAmount: subTotal + taxAmount };
 }, [items, selectedCwo]);

 // Allowed billing amount preview
 const allowedBillingAmount = useMemo(() => {
 if (!selectedStages.length || !selectedCwo) return null;
 const total = (selectedCwo.selectedBoqItems || []).reduce((sum, item) => {
 const pct = getTotalPercentage(selectedStages, item, selectedCwo);
 return sum + (Number(item.quantity || 0) * Number(item.price || 0) * pct) / 100;
 }, 0);
 return total;
 }, [selectedStages, selectedCwo]);

 const toggleStage = (stage) => {
 setSelectedStages((prev) =>
 prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage]
 );
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!selectedCwoId) return swal("Error", "Select approved CWO", "error");
 if (!items.length || totals.subTotal <= 0) {
 return swal("Error", "BOQ amount should be greater than zero", "error");
 }

 const invalidItem = items.find(
 (item) =>
 Number(item.quantity || 0) <= 0 ||
 Number(item.quantity || 0) > Number(item.remainingQuantity || 0) ||
 (!selectedStages.length && Number(item.price || 0) > Number(item.originalPrice || 0))
 );
 if (invalidItem) {
 return swal("Error", `${invalidItem.itemName} — quantity exceeds remaining or rate is invalid`, "error");
 }

 if (selectedStages.length > 0) {
 const noTermItem = items.find((item) => !item.totalPercent || item.totalPercent === 0);
 if (noTermItem) {
 return swal("Missing Payment Term", `No payment term for selected stages on item "${noTermItem.itemName}"`, "error");
 }
 }

 try {
 setSaving(true);
 const data = new FormData();
 data.append("contractualWorkOrder", selectedCwoId);
 data.append("invoiceNo", invoiceNo);
 data.append("invoiceDate", invoiceDate);
 data.append("remarks", remarks);
 data.append("items", JSON.stringify(items));
 if (selectedStages.length > 0) data.append("stages", JSON.stringify(selectedStages));
 if (billFile) data.append("billFile", billFile);

 const res = await api.post("/contractor-billings", data, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 swal("Success", "Contractor billing submitted to accounts", "success");
 const workOrderId =
 selectedCwo?.workOrder?._id || selectedCwo?.workOrder || workOrderFromUrl;
 navigate(
 workOrderId
 ? `/contractor-billing?workOrder=${workOrderId}`
 : "/contractor-billing"
 );
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to save bill", "error");
 } finally {
 setSaving(false);
 }
 };

 const updateItemQuantity = (index, value) => {
 setItems((prev) =>
 prev.map((item, itemIndex) => {
 if (itemIndex !== index) return item;
 const quantity = Math.min(
 Math.max(Number(value || 0), 0),
 Number(item.remainingQuantity || 0)
 );
 return { ...item, quantity };
 })
 );
 };

 const removeItem = (index) => {
 setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
 };

 if (loading) {
 return <div className="text-center py-5"><Spinner /></div>;
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Contractor Billing" motherMenu="Procurement" />
 <Form onSubmit={handleSubmit}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Contractor Billing</Card.Title>
 <Button variant="light" size="sm" onClick={() => navigate(-1)}>Back</Button>
 </Card.Header>
 <Card.Body>
 <Row className="g-3 mb-4">
 <Col md={5}>
 <Form.Label>Approved Contractual WO</Form.Label>
 <div className="form-control bg-light fw-bold d-flex align-items-center">
 {selectedCwo
 ? `${selectedCwo.cwoNo} — ${selectedCwo.vendor?.name || "Vendor"}`
 : "No approved CWO selected"}
 </div>
 </Col>
 <Col md={4}>
 <Form.Label>Vendor</Form.Label>
 <div className="form-control bg-light fw-bold d-flex align-items-center">
 {selectedCwo?.vendor?.name || "—"}
 </div>
 </Col>
 <Col md={3}>
 <Form.Label>Status</Form.Label>
 <div><Badge bg="info">Submit to Accounts</Badge></div>
 </Col>

 {/* Stage Selection */}
 <Col md={12}>
 <Form.Label className="fw-semibold">
 Billing Stage / Milestone
 <small className="text-muted fw-normal ms-1">(select one or more)</small>
 </Form.Label>
 {availableStages.length > 0 ? (
 <div className="border rounded p-3 d-flex flex-wrap gap-3">
 {availableStages.map((s) => {
 const pct = getTermPercentage(s, null, selectedCwo);
 return (
 <Form.Check
 key={s}
 type="checkbox"
 id={`stage-${s}`}
 label={
 <span>
 {s}
 {pct > 0 && (
 <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: 10 }}>
 {pct}%
 </Badge>
 )}
 </span>
 }
 checked={selectedStages.includes(s)}
 onChange={() => toggleStage(s)}
 />
 );
 })}
 </div>
 ) : (
 <div className="form-control bg-light text-muted">
 No payment terms set on this CWO — rates will be entered manually
 </div>
 )}
 </Col>

 {/* Allowed Amount Preview */}
 {selectedStages.length > 0 && allowedBillingAmount !== null && (
 <Col md={12}>
 <div className="border rounded p-2 bg-light d-flex align-items-center gap-3">
 <div>
 <div className="small text-muted">Selected stages</div>
 <div className="fw-bold">{selectedStages.join(" + ")}</div>
 </div>
 <div className="ms-auto text-end">
 <div className="small text-muted">Allowed billing amount</div>
 <div className="fw-bold text-primary fs-6">{money(allowedBillingAmount)}</div>
 </div>
 </div>
 </Col>
 )}

 <Col md={4}>
 <Form.Label>Bill / Invoice No</Form.Label>
 <Form.Control value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Bill Date</Form.Label>
 <Form.Control type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Upload Contractor Bill</Form.Label>
 <Form.Control type="file" onChange={(e) => setBillFile(e.target.files?.[0] || null)} />
 </Col>
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control value={remarks} onChange={(e) => setRemarks(e.target.value)} />
 </Col>
 </Row>

 <div className="table-responsive">
 <Table bordered hover>
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>WLINO</th>
 <th>BOQ Item</th>
 <th>Unit</th>
 <th>JMR Approved</th>
 <th>Qty</th>
 <th>Already Billed</th>
 <th>Remaining</th>
 {selectedStages.length > 0 && <th className="text-center">Total %</th>}
 <th>Rate</th>
 <th>Amount</th>
 <th></th>
 </tr>
 </thead>
 <tbody>
 {items.map((item, index) => (
 <tr key={item.boqItemId || index}>
 <td>{index + 1}</td>
 <td>{item.wlino || "-"}</td>
 <td>
 <strong>{item.itemName}</strong>
 <div className="small text-muted">{item.description}</div>
 </td>
 <td>{item.unit}</td>
 <td>
 <span className="fw-bold text-info">{item.jmrApprovedQty || 0}</span>
 {item.jmrApprovedQty > 0 && (
 <div className="small text-muted">From JMR</div>
 )}
 </td>
 <td style={{ width: 120 }}>
 <Form.Control
 type="number"
 min="0"
 max={item.remainingQuantity}
 step="any"
 size="sm"
 value={item.quantity}
 onChange={(e) => updateItemQuantity(index, e.target.value)}
 />
 </td>
 <td>{item.alreadyBilledQuantity}</td>
 <td>
 <span className="fw-bold text-success">{item.remainingQuantity}</span>
 </td>
 {selectedStages.length > 0 && (
 <td className="text-center">
 <span className={`badge ${item.totalPercent > 0 ? "bg-success" : "bg-danger"}`}>
 {item.totalPercent > 0 ? `${item.totalPercent}%` : "No term"}
 </span>
 </td>
 )}
 <td style={{ width: 160 }}>
 {selectedStages.length > 0 && item.totalPercent > 0 ? (
 <>
 <div className="fw-bold text-success">{money(item.price)}</div>
 <div className="small text-muted">CWO: {money(item.originalPrice)} × {item.totalPercent}%</div>
 </>
 ) : (
 <div className="fw-bold">{money(item.price)}</div>
 )}
 </td>
 <td>{money(Number(item.quantity || 0) * Number(item.price || 0))}</td>
 <td className="text-center">
 <Button variant="outline-danger" size="sm" onClick={() => removeItem(index)}>
 <i className="fa fa-trash"></i>
 </Button>
 </td>
 </tr>
 ))}
 {items.length === 0 && (
 <tr>
 <td colSpan={selectedStages.length > 0 ? "12" : "11"} className="text-center text-muted py-4">
 No remaining BOQ quantity available for billing.
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>

 <Row className="justify-content-end">
 <Col md={4}>
 <div className="border rounded p-3 bg-light">
 <div className="d-flex justify-content-between">
 <span>Sub Total</span>
 <strong>{money(totals.subTotal)}</strong>
 </div>
 {selectedCwo?.cgst > 0 && (
 <div className="d-flex justify-content-between">
 <span>CGST ({selectedCwo.cgst}%)</span>
 <strong>{money(totals.cgstAmount)}</strong>
 </div>
 )}
 {selectedCwo?.sgst > 0 && (
 <div className="d-flex justify-content-between">
 <span>SGST ({selectedCwo.sgst}%)</span>
 <strong>{money(totals.sgstAmount)}</strong>
 </div>
 )}
 {selectedCwo?.igst > 0 && (
 <div className="d-flex justify-content-between">
 <span>IGST ({selectedCwo.igst}%)</span>
 <strong>{money(totals.igstAmount)}</strong>
 </div>
 )}
 <hr />
 <div className="d-flex justify-content-between text-primary">
 <span>Total</span>
 <strong>{money(totals.totalAmount)}</strong>
 </div>
 </div>
 </Col>
 </Row>
 </Card.Body>
 <Card.Footer className="text-end bg-white">
 <Button type="submit" disabled={saving}>
 {saving ? "Saving..." : "Save Contractor Bill"}
 </Button>
 </Card.Footer>
 </Card>
 </Form>
 </Fragment>
 );
};

export default ContractorBillingAdd;
